export class InternalArtStates {
  static OPEN = 'OPEN';
  static PEND_C = 'PEND_C';
  static PEND_S_ART = 'PEND_S_ART';
  static PEND_S_CSR = 'PEND_S_CSR';
  static PEND_S_OUT = 'PEND_S_OUT';
  static APPROVED = 'APPROVED';
  static CANCELED = 'CANCELED';
  static REVIEW = 'REVIEW';
}

export class SizeStandard {
  width!: string;
  height?: string;
}

export class ProductType {
  name!: string;
}

export class ProductSubtype {
  name!: string;
}

export class Backing {
  name!: string;
}

export class DesignType {
  imageSrc?: string;
}

export class WizardInput {
  id?: string;
  originalDocID?: string;
  DesignType?: DesignType;
  ProductType?: ProductType;
  ProductSubtype?: ProductSubtype;
  SizeStandard?: SizeStandard;
  Backing?: Backing;
}

export class ProductTypeGenerator {
  static productToBCMap: Record<string, Record<string, string>> = {
    "Chenille": { "Chenille": "_CHEN_PATCH" },
    "Embroidery": {
      "3D Emblems": "_3D_EMB_PATCH",
      "Appliques": "_EMB_PATCH",
      "Woven": "_WOVEN_PATCH"
    },
    "Flexbroidery": { "Flexbroidery": "_FLEXBROIDERY" },
    "Leather Emblems": { "Faux Leather": "_FAUX_LEATHER_PATCH" },
    "FlexStyle": {
      "Faux Leather": "_FAUX_LEATHER_PATCH",
      "Beveled": "_FLEX_STYLE",
      "Domed": "_FLEX_STYLE",
      "Flat": "_FLEX_STYLE",
      "Holographic": "_FLEX_STYLE",
      "Textured": "_FLEX_STYLE"
    },
    "PV+": { "PV+ Flat": "_PVC_PATCH" },
    "Sublimation / Perfect Print": {
      "Print Stitch Emblems": "_PRINT_STITCH_PATCH",
      "Woven - Patches": "_WOVEN_PATCH"
    }
  };
  
  static bcData: Record<string, { sizes: number[]; suffixes: string[] }> = {
    "_3D_EMB_PATCH": {
      sizes: [0.01, 1.01, 1.51, 2.01, 2.51, 3.01, 3.51, 4.01, 4.51, 5.01,
              6.01, 7.01, 8.01, 9.01, 10.01, 11.01],
      suffixes: ["HA"]
    },
    "_ARTPROOF_PATCHES": { sizes: [0.01, 2.01, 4.01, 6.01, 8.01, 10.01], suffixes: ["D"] },
    "_ARTPROOF_PATCHES_S": { sizes: [0.01, 2.01, 4.01, 6.01, 8.01, 10.01], suffixes: ["D"] },
    "_CHEN_PATCH": {
      sizes: [0.01, 2.00, 3.01, 4.01, 5.01, 6.01, 7.01, 8.01, 9.01,
              10.01, 11.01, 12.01],
      suffixes: ["HA"]
    },
    "_FAUX_LEATHER_PATCH": {
      sizes: [0.01, 1.51, 2.01, 2.51, 3.01, 3.51, 4.01, 4.51],
      suffixes: ["HA", "PS"]
    },
    "_WOVEN_PATCH": {
      sizes: [0.01, 1.51, 2.01, 2.51, 3.01, 3.51, 4.01, 4.51, 5.01, 6.01],
      suffixes: ["HA"]
    },
    "_FLEX_STYLE": {
      sizes: [0.01, 1.01, 1.51, 2.01, 2.51, 3.01, 3.51, 4.01, 4.51,
              5.01, 5.51],
      suffixes: ["HA", "PS"]
    },
    "_LEATHER_PATCH": {
      sizes: [0.01, 1.51, 2.01, 2.51, 3.01, 3.51, 4.01, 4.51],
      suffixes: ["HA", "PS"]
    },
    "_PRINT_STITCH_PATCH": {
      sizes: [0.01, 1.01, 1.51, 2.01, 2.51, 3.01, 3.51, 4.01, 4.51,
              5.01, 6.01, 7.01, 8.01, 9.01, 10.01, 11.01],
      suffixes: ["HA"]
    },
    "_PVC_PATCH": {
      sizes: [0.01, 1.51, 2.01, 2.51, 3.01, 3.51],
      suffixes: ["HA", "PS"]
    }
  };

  static getBCItemNo(productClass: string, productType: string): string | null {
    if (!this.productToBCMap[productClass]) return null;
    return this.productToBCMap[productClass][productType] || null;
  }

  static getBCVariant(bcItem: string, size: number, suffix: string): string | null {
    if (!this.bcData[bcItem]) return null;
    if (!this.bcData[bcItem].sizes.includes(size)) return null;
    if (!this.bcData[bcItem].suffixes.includes(suffix)) return null;
    return `${size}${suffix}`;
  }

  static getArtproofVariant(bcItem: string, size: number): string {
    const variantMapping: Record<string, string[]> = {
      "_3D_EMB_PATCH": ["3D0.01", "3D10.01", "3D2.01", "3D4.01", "3D6.01", "3D8.01"],
      "_CHEN_PATCH": ["CHEN"],
      "_EMB_PATCH": ["EMP0.01", "EMP10.01", "EMP2.01", "EMP4.01", "EMP6.01", "EMP8.01"],
      "_FAUX_LEATHER_PATCH": ["FAUX LTHR"],
      "_FAUX_SUEDE_PATCH": ["FAUX SUEDE"],
      "_FLEXBROIDERY": ["FLEXBROID"],
      "_FLEX_STYLE": ["FLEXSTYLE"],
      "_LEATHER_PATCH": ["LEATHER"],
      "_PRINT_STITCH_PATCH": ["PRINT0.01", "PRINT10.01", "PRINT2.01", "PRINT4.01", "PRINT6.01", "PRINT8.01"],
      "_PVC_PATCH": ["PVC"],
      "_WOVEN_PATCH": ["WOVEN"]
    };
    const variants = variantMapping[bcItem];
    if (!variants) {
      return `Error: Invalid BC item '${bcItem}'`;
    }
    let chosenVariant: string | null = null;
    let maxVariantNum = -Infinity;
    const numericVariants = variants.filter((v) => /(\d+\.\d+)/.test(v));
    const nonNumericVariants = variants.filter((v) => !/(\d+\.\d+)/.test(v));
    for (const v of numericVariants) {
      const match = v.match(/(\d+\.\d+)/);
      if (match) {
        const variantNum = parseFloat(match[1]);
        if (variantNum <= size && variantNum > maxVariantNum) {
          chosenVariant = v;
          maxVariantNum = variantNum;
        }
      }
    }
    if (!chosenVariant) {
      if (nonNumericVariants.length > 0) {
        chosenVariant = nonNumericVariants[0];
      } else {
        if (variants.length > 0) {
          chosenVariant = variants[0];
        } else {
          return `Error: No valid variants for '${bcItem}' at size ${size}`;
        }
      }
    }
    return chosenVariant;
  }
  

  static getVariantFromObject(input: WizardInput): { itemNo: string; variantId: string | null } | null {
    const productClass = input.ProductType?.name?.includes("Chenille") ? "Chenille" : null;
    const productType = input.ProductSubtype?.name || null;
    if (!productClass || !productType) return null;
    const bcItem = '_3D_EMB_PATCH';  //this.getBCItemNo(productClass, productType);
    if (!bcItem) return null;
    const size = parseFloat(input.SizeStandard?.width || "") || 0;
    const suffix = input.Backing?.name?.includes("Heat Applied") ? "HA" : "PS";
    return {
      itemNo: bcItem,
      variantId: this.getBCVariant(bcItem, size, suffix)
    };
  }
}

function calculateDiagonal(width: string, height: string ) {
  const w = parseFloat(width);
  const h = parseFloat(height);
  return Math.sqrt(w**2 + h**2);
}

export function getPriceAndDiscount(prices: any, size:any, flag:string, desiredQuantity: number) {
  size = calculateDiagonal(size.width, size.height);
  size = parseFloat(size.toFixed(2));
  const validItems = prices.map((item:any) => {
    const match = item.variant.id.match(/(\d+(?:\.\d+)?)([A-Za-z]+)/);
    if (!match) return null;
    const numericPart = parseFloat(match[1]);
    const suffix = match[2];
    if (suffix === flag && numericPart <= size) {
      return { numericPart, price: item.price, discountPercent: item.discountPercent };
    }
    return null;
  }).filter(Boolean);
  if (!validItems.length) return null;
  validItems.sort((a:any, b:any) => b.numericPart - a.numericPart);
  const bestItem = validItems[0];
  console.log(desiredQuantity);
  return { price: bestItem.price, discountPercent: bestItem.discountPercent };
}

export class DesignDoc {
  id!: string;
  docType!: string;
  wizardID!: string;
  statusID!: string;
  customerID?: string;
  BoM!: Record<string, unknown>;
  createdDate!: string;
  updateDate!: string;
  itemNo?: string | null;
  variantId?: string | null;
  quantity!: number;
  wizardData!: WizardInput;
  originalArtwork!: Array<{
    url: string;
    date: string;
    fileRole: string;
  }>;
  productionArtwork!: unknown[];
  productionArtworkPreview!: unknown[];
  mappedQuantity!: string;
  lineID!: string | null;
  orderID!: string | null;
  lineN!: number | null;
  orderN!: number | null;
  cloned!: boolean;
  originalDocID?: string;
  isLNProof!: boolean;
  addedToCartDate?: string;
  archived!: boolean;
  deleted!: boolean;
  internalID?: string;

  static transformToDesignDoc(input: WizardInput, customerID: string): DesignDoc {
    const doc = new DesignDoc();
    const originalArtwork = (input.DesignType && input.DesignType.imageSrc)
      ? [{
          url: input.DesignType.imageSrc,
          date: new Date().toISOString(),
          fileRole: "DefaultFile"
        }]
      : [];
    const productType = ProductTypeGenerator.getVariantFromObject(input);
    doc.id = input.id || "";
    doc.docType = "WizardDesign";
    doc.wizardID = "EmblemWizard";
    doc.statusID = InternalArtStates.OPEN;
    doc.customerID = customerID;
    doc.BoM = {};
    doc.createdDate = new Date().toISOString();
    doc.updateDate = new Date().toISOString();
    doc.itemNo = productType?.itemNo || null;
    doc.variantId = productType?.variantId || null;
    doc.quantity = 0;
    doc.wizardData = input;
    doc.originalArtwork = originalArtwork;
    doc.productionArtwork = [];
    doc.productionArtworkPreview = [];
    doc.mappedQuantity = "";
    doc.lineID = null;
    doc.orderID = null;
    doc.lineN = null;
    doc.orderN = null;
    doc.cloned = false;
    doc.originalDocID = input.originalDocID;
    doc.isLNProof = false;
    doc.addedToCartDate = undefined;
    doc.archived = false;
    doc.deleted = false;
    doc.internalID = input.originalDocID;
    return doc;
  }
}
